import DebouncedResizeContainer from "@/components/DebouncedResizeContainer";
import { LogOut, WifiOff } from "lucide-react";
import { memo, useMemo, useState } from "react";
import type { PlayerDropsSummaryHour } from "@shared/otherTypes";
import { PlayerDropsLoadingSpinner } from "./PlayerDropsGenericSubcards";
import TimelineDropsChart, { HoverInfoPanel, TimelineDropsChartData, HoverDatumType } from "./TimelineDropsChart";
import { processDropsSummary } from "./chartingUtils";
import { DisplayLodType, DrilldownRangeSelectionType } from "./PlayerDropsPage";

type PlayerDropsTimelineChartsProps = {
    isError?: boolean;
    dataTs?: number;
    summaryData?: PlayerDropsSummaryHour[];
    rangeSelected: DrilldownRangeSelectionType;
    rangeSetter: (range: DrilldownRangeSelectionType) => void;
    displayLod: DisplayLodType;
    setDisplayLod: (range: DisplayLodType) => void;
};

const TimelineCard = memo(({
    isError,
    dataTs,
    summaryData,
    rangeSelected,
    rangeSetter,
    displayLod,
    setDisplayLod
}: PlayerDropsTimelineChartsProps) => {
    const [expectedDropsChartSize, setExpectedDropsChartSize] = useState({ width: 0, height: 0 });
    const [unexpectedDropsChartSize, setUnexpectedDropsChartSize] = useState({ width: 0, height: 0 });
    const [expectedHoverDatum, setExpectedHoverDatum] = useState<HoverDatumType>(null);
    const [unexpectedHoverDatum, setUnexpectedHoverDatum] = useState<HoverDatumType>(null);

    //Process data only once
    const chartsData = useMemo(() => {
        if (!summaryData || !dataTs) return;
        const startDate = new Date(dataTs);
        const endDate = new Date(dataTs);
        if (displayLod === 'day') {
            // 14d window, 12h+15m padding start
            startDate.setHours(-(14 * 24) - 12, -15, 0, 0);
            endDate.setHours(12, 0, 0, 0);
        } else {
            // 7d window, 30m+15m padding start
            startDate.setHours(startDate.getHours() - 7 * 24, -45, 0, 0);
            endDate.setMinutes(30, 0, 0);
        }
        const processed = processDropsSummary(summaryData, displayLod, startDate);
        if (!processed) return;

        const commonProps = { displayLod, startDate, endDate };
        return {
            expected: {
                ...commonProps,
                maxDrops: processed.expectedSeriesMax,
                categoriesSorted: processed.expectedCategoriesSorted,
                log: processed.expectedSeries,
            } satisfies TimelineDropsChartData,
            unexpected: {
                ...commonProps,
                maxDrops: processed.unexpectedSeriesMax,
                categoriesSorted: processed.unexpectedCategoriesSorted,
                log: processed.unexpectedSeries,
            } satisfies TimelineDropsChartData,
        }
    }, [summaryData, displayLod]);

    return (
        <div className="w-full flex flex-col gap-10">
            {/* Standard Session Exits (Expected) */}
            <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between border-b border-white/5 pb-2">
                    <div className="flex items-center gap-2 text-scPurple">
                        <LogOut className="size-4" />
                        <h3 className="font-mono text-xs md:text-sm font-bold tracking-wider uppercase">Standard Session Exits</h3>
                    </div>
                </div>
                <div className="h-48 max-h-48 w-full bg-gradient-to-b from-white/[0.02] to-transparent border border-white/5 rounded-xl p-2 relative overflow-hidden">
                    <DebouncedResizeContainer onDebouncedResize={setExpectedDropsChartSize}>
                        {chartsData ? (
                            <TimelineDropsChart
                                chartData={chartsData.expected}
                                chartName="expected"
                                width={expectedDropsChartSize.width}
                                height={expectedDropsChartSize.height}
                                rangeSelected={rangeSelected}
                                rangeSetter={rangeSetter}
                                onHoverDatum={setExpectedHoverDatum}
                            />
                        ) : (
                            <PlayerDropsLoadingSpinner isError={isError} />
                        )}
                    </DebouncedResizeContainer>
                </div>
                {/* Hover Info Panel - rendered below the chart */}
                {chartsData && (
                    <HoverInfoPanel
                        hoverDatum={expectedHoverDatum}
                        categories={chartsData.expected.categoriesSorted}
                    />
                )}
            </div>

            {/* Unscheduled Disconnections (Unexpected) */}
            <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between border-b border-white/5 pb-2">
                    <div className="flex items-center gap-2 text-scCyan">
                        <WifiOff className="size-4 animate-pulse" />
                        <h3 className="font-mono text-xs md:text-sm font-bold tracking-wider uppercase">Unscheduled Disconnections</h3>
                    </div>
                </div>
                <div className="h-64 max-h-64 w-full bg-gradient-to-b from-white/[0.02] to-transparent border border-white/5 rounded-xl p-2 relative overflow-hidden">
                    <DebouncedResizeContainer onDebouncedResize={setUnexpectedDropsChartSize}>
                        {chartsData ? (
                            <TimelineDropsChart
                                chartData={chartsData.unexpected}
                                chartName="unexpected"
                                width={unexpectedDropsChartSize.width}
                                height={unexpectedDropsChartSize.height}
                                rangeSelected={rangeSelected}
                                rangeSetter={rangeSetter}
                                onHoverDatum={setUnexpectedHoverDatum}
                            />
                        ) : (
                            <PlayerDropsLoadingSpinner isError={isError} />
                        )}
                    </DebouncedResizeContainer>
                </div>
                {/* Hover Info Panel - rendered below the chart */}
                {chartsData && (
                    <HoverInfoPanel
                        hoverDatum={unexpectedHoverDatum}
                        categories={chartsData.unexpected.categoriesSorted}
                    />
                )}
            </div>
        </div>
    );
});

export default memo(TimelineCard);
